import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAlert } from '../hooks/useAlert';
import Header from '../components/Header';
import './PaymentSetup.css';

function PaymentSetup() {
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState({
    free: {
      name: 'Free',
      price: '$0',
      priceId: null,
      features: [
        'Unlimited transcriptions',
        '1 week transcript retention',
        'Standard processing priority',
        'Share transcripts',
        'Download in text format',
        'Community support'
      ],
      color: '#9E9E9E'
    },
    basic: {
      name: 'Basic',
      price: '$2',
      priceId: 'price_1S0nbIFAxk5A4QdQ49ymEONG',
      features: [
        'Unlimited transcriptions',
        '3 months transcript retention',
        'Higher processing priority',
        'Share transcripts',
        'Download in multiple formats',
        'Email support'
      ],
      color: '#4CAF50'
    },
    premium: {
      name: 'Premium',
      price: '$8',
      priceId: 'price_1S0nbnFAxk5A4QdQxnudPNg5',
      features: [
        'Unlimited transcriptions',
        'Unlimited transcript retention',
        'Highest processing priority',
        'Live transcription',
        'Share transcripts',
        'Download in multiple formats',
        'Priority support'
      ],
      color: '#FF9800',
      recommended: true
    }
  });
  const { user, isAuthenticated, updateUser } = useAuth();
  const { showConfirm } = useAlert();
  const navigate = useNavigate();

  // Fetch actual prices from Stripe
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await fetch('/api/stripe/prices');
        if (response.ok) {
          const prices = await response.json();
          setPlans(prev => ({
            ...prev,
            basic: {
              ...prev.basic,
              price: `$${prices.basic.amount}`,
              priceId: prices.basic.id
            },
            premium: {
              ...prev.premium,
              price: `$${prices.premium.amount}`,
              priceId: prices.premium.id
            }
          }));
        }
      } catch (err) {
        console.error('Failed to fetch prices from Stripe:', err);
        // Keep default prices if fetch fails
      }
    };

    fetchPrices();
  }, []);

  // NEW: Helper function to check authentication and redirect if needed
  const checkAuthAndRedirect = (action = null, plan = null) => {
    if (!isAuthenticated) {
      const state = { from: '/plans-and-payment' };
      if (action) state.action = action;
      if (plan) state.plan = plan;
      
      // Redirect to your staging auth URL
      window.location.href = 'https://staging.rec-chord.com:8080/auth';
      return false;
    }
    return true;
  };

  const handleSubscribe = async () => {
    // Check authentication before proceeding
    if (!checkAuthAndRedirect('subscribe', selectedPlan)) return;

    setIsLoading(true);
    setError('');

    try {
      const plan = plans[selectedPlan];

      // Create Stripe Checkout session
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          priceId: plan.priceId,
          tier: selectedPlan
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout process');
      setIsLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    // Check authentication before proceeding
    if (!checkAuthAndRedirect('update-payment')) return;

    setIsLoading(true);
    setError('');

    try {
      // Open Stripe Customer Portal for payment method management
      const response = await fetch('/api/stripe/create-portal-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open payment settings');
      }

      const { url } = await response.json();

      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to open payment settings');
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    // Check authentication before proceeding
    if (!checkAuthAndRedirect('cancel-subscription')) return;

    const confirmed = await showConfirm(
      'Cancel Subscription',
      'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.',
      {
        confirmText: 'Yes, Cancel',
        cancelText: 'Keep Subscription'
      }
    );

    if (!confirmed) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Show success message using alert
        await showConfirm(
          'Subscription Cancelled',
          data.message || 'Your subscription has been cancelled and will end at the end of the current billing period.',
          {
            confirmText: 'OK',
            showCancel: false
          }
        );

        // Refresh user data
        if (updateUser) {
          await updateUser();
        }

        // Reload the page to show updated subscription status
        window.location.reload();
      } else {
        console.error('Cancel subscription error:', data);
        setError(data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Cancel subscription exception:', err);
      setError('Failed to cancel subscription: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Show all plans with current plan highlighted
  const currentTier = user?.tier || 'free';

  const handleDowngradeToFree = async () => {
    // Check authentication before proceeding
    if (!checkAuthAndRedirect('downgrade-to-free')) return;

    const confirmed = await showConfirm(
      'Downgrade to Free Plan',
      'Are you sure you want to downgrade to the Free plan? You will lose access to premium features at the end of your billing period.',
      {
        confirmText: 'Yes, Downgrade',
        cancelText: 'Keep Current Plan'
      }
    );

    if (!confirmed) {
      return;
    }

    await handleCancelSubscription();
  };

  // NEW: Handle "Get Rec-Chording!" button click
  const handleGetRecording = () => {
    // Check authentication before redirecting to home
    if (!checkAuthAndRedirect('start-recording')) return;
    
    navigate('/');
  };

  // NEW: Handle plan selection with authentication check
  const handlePlanSelection = (planKey) => {
    // Check authentication before allowing plan selection
    if (!checkAuthAndRedirect('select-plan', planKey)) return;
    
    setSelectedPlan(planKey);
  };

  // Show all plans view
  return (
    <div className="payment-page">
      <Header />

      <main className="payment-main">
        <div className="plans-container">
          {Object.entries(plans).map(([key, plan]) => {
            const isCurrentPlan = currentTier === key;
            const canSelect = !isCurrentPlan;

            return (
              <div
                key={key}
                className={`plan-card ${selectedPlan === key ? 'selected' : ''} ${plan.recommended ? 'recommended' : ''} ${isCurrentPlan ? 'current-plan' : ''}`}
                onClick={() => canSelect && handlePlanSelection(key)}
                style={{
                  borderColor: isCurrentPlan ? plan.color : (selectedPlan === key ? plan.color : undefined),
                  cursor: canSelect ? 'pointer' : 'default'
                }}
              >
                {plan.recommended && <div className="recommended-badge">RECOMMENDED</div>}
                {isCurrentPlan && <div className="current-badge">CURRENT PLAN</div>}

                <h2>{plan.name}</h2>
                <div className="plan-price">
                  <span className="price">{plan.price}</span>
                  <span className="period">/month</span>
                </div>

                <ul className="plan-features">
                  {plan.features.map((feature, index) => (
                    <li key={index}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {!isCurrentPlan && key !== 'free' && (
                  <button
                    className={`select-plan-btn ${selectedPlan === key ? 'selected' : ''}`}
                    style={{ backgroundColor: selectedPlan === key ? plan.color : undefined }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanSelection(key);
                    }}
                  >
                    {selectedPlan === key ? 'Selected' : 'Select Plan'}
                  </button>
                )}

                {!isCurrentPlan && key === 'free' && currentTier !== 'free' && (
                  <button
                    className="select-plan-btn downgrade"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDowngradeToFree();
                    }}
                    disabled={isLoading}
                  >
                    Downgrade to Free
                  </button>
                )}

                {isCurrentPlan && key !== 'free' && (
                  <div className="current-plan-actions">
                    <button
                      className="manage-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdatePaymentMethod();
                      }}
                      disabled={isLoading}
                    >
                      Update Payment
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelSubscription();
                      }}
                      disabled={isLoading}
                    >
                      Cancel Plan
                    </button>
                  </div>
                )}

                {isCurrentPlan && key === 'free' && (
                  <button
                    className="start-recording-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGetRecording();
                    }}
                  >
                    Get Rec-Chording! 🎙️
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {selectedPlan && selectedPlan !== currentTier && selectedPlan !== 'free' && (
          <div className="checkout-section">
            <button
              className="subscribe-btn"
              onClick={handleSubscribe}
              disabled={isLoading}
              style={{ backgroundColor: plans[selectedPlan]?.color }}
            >
              {isLoading ? 'Processing...' : `Subscribe to ${plans[selectedPlan]?.name} Plan`}
            </button>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <p className="payment-note">
              🔒 Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default PaymentSetup;
